// src/pages/admin/ReportSelectionPage.js
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDepartment } from '../../contexts/DepartmentContext';
import { useStaff } from '../../contexts/StaffContext';
import ReportCategorySelector from '../../components/reports/ReportCategorySelector';

const ReportSelectionPage = () => {
  const { t } = useTranslation('reports');
  const departmentContext = useDepartment();
  const staffContext = useStaff();
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [departmentStaffCounts, setDepartmentStaffCounts] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('time-tracking');

  useEffect(() => {
    const loadDepartmentsAndStaff = async () => {
      setLoading(true);
      try {
        let deps = [];
        if (departmentContext.departments) {
          deps = departmentContext.departments;
        } else if (typeof departmentContext.getDepartments === 'function') {
          deps = await departmentContext.getDepartments();
        } else if (typeof departmentContext.loadDepartments === 'function') {
          await departmentContext.loadDepartments();
          deps = departmentContext.departments || [];
        }

        let staffMembers = [];
        if (staffContext.staff) {
          staffMembers = staffContext.staff;
        } else if (typeof staffContext.getStaff === 'function') {
          staffMembers = await staffContext.getStaff();
        } else if (typeof staffContext.loadStaff === 'function') {
          await staffContext.loadStaff();
          staffMembers = staffContext.staff || [];
        }

        const staffCounts = {};
        deps.forEach(department => {
          const staffCount = staffMembers.filter(staff =>
            staff.departmentIds && staff.departmentIds.includes(department.id)
          ).length;

          const fallbackCount = department.staffIds ? department.staffIds.length : 0;
          staffCounts[department.id] = Math.max(staffCount, fallbackCount);
        });

        setDepartments(deps);
        setDepartmentStaffCounts(staffCounts);
      } catch (error) {
        console.error('Error loading departments and staff:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDepartmentsAndStaff();
  }, [departmentContext, staffContext]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>{t('loadingDepartments')}</p>
      </div>
    );
  }

  return (
    <div className="report-selection-page">
      <div className="page-header">
        <h1>{t('reportsTitle')}</h1>
        <p>{t('pageSubtitle')}</p>
      </div>

      <ReportCategorySelector
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
      />

      <div className="department-grid">
        {departments.length === 0 ? (
          <div className="empty-state">
            <p>{t('noDepartmentsFound')}</p>
            <Link to="/admin/departments" className="primary-button">
              {t('goToDepartments')}
            </Link>
          </div>
        ) : (
          departments.map(department => (
            <div key={department.id} className="department-card">
              <h3>{department.name}</h3>
              <p className="department-staff-count">
                {t('staffMembers', { count: departmentStaffCounts[department.id] || 0 })}
              </p>
              <Link
                to={`/admin/reports/${selectedCategory}/${department.id}`}
                className="view-reports-btn"
              >
                {t('viewReports')}
              </Link>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ReportSelectionPage;